// =============================================================================
// jmpLive — the open journey plans, read the same way everywhere (mig 445)
// =============================================================================
// 22 Oct 2026, road week item 8. One hook over v_jmp_live, one card, and the
// three actions (start / check in / complete) as the app.* RPCs, so the Site
// Folder, the field home, the list and the detail page all say the same
// thing: whose journey, where to, when the next check-in is due, and whether
// it's late.
// =============================================================================

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { CheckCircle2, Clock, AlertTriangle, Navigation, ChevronRight } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui";

export interface JmpLive {
  jmp_id: string;
  jmp_number: string;
  status: "planned" | "active" | "escalated" | "overdue";
  project_id: string | null;
  site_id: string | null;
  traveller_employee_id: string;
  traveller_name: string;
  depart_location: string;
  arrive_location: string;
  depart_at: string;
  eta: string;
  return_at: string | null;
  checkin_interval_minutes: number;
  escalation_grace_minutes: number;
  next_due: string | null;
  next_state: "ok" | "due" | "missed" | null;
  last_checked_in_at: string | null;
  checkins_done: number;
}

/** Every open plan (RLS is open on the JMP tables — a crew can see each other's journeys). */
export function useJmpLive(enabled = true) {
  return useQuery({
    queryKey: ["jmp_live"],
    enabled,
    refetchInterval: 60_000,
    queryFn: async () => {
      const { data, error } = await (supabase as any).from("v_jmp_live").select("*").order("depart_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as JmpLive[];
    },
  });
}

/** The plan the Site Folder should route to: mine on this project first, then anyone's on this project. */
export function pickFolderJmp(rows: JmpLive[], projectId: string | null | undefined, me: string | null | undefined): JmpLive | null {
  if (!projectId) return null;
  const here = rows.filter(r => r.project_id === projectId);
  return here.find(r => r.traveller_employee_id === me) ?? here[0] ?? null;
}

export function useJmpActions() {
  const qc = useQueryClient();
  const done = () => { qc.invalidateQueries({ queryKey: ["jmp_live"] }); qc.invalidateQueries({ queryKey: ["jmp"] }); qc.invalidateQueries({ queryKey: ["jmp_checkins"] }); qc.invalidateQueries({ queryKey: ["jmps"] }); };
  const rpc = async (fn: string, args: Record<string, unknown>) => {
    const { data, error } = await (supabase as any).schema("app").rpc(fn, args);
    if (error) throw new Error(error.message);
    return data;
  };
  const start    = useMutation({ mutationFn: (v: { jmp_id: string }) => rpc("jmp_start", { p_jmp_id: v.jmp_id }), onSuccess: done });
  const checkIn  = useMutation({ mutationFn: (v: { jmp_id: string; lat?: number | null; lng?: number | null; notes?: string | null }) =>
    rpc("jmp_check_in", { p_jmp_id: v.jmp_id, p_lat: v.lat ?? null, p_lng: v.lng ?? null, p_notes: v.notes ?? null }), onSuccess: done });
  const complete = useMutation({ mutationFn: (v: { jmp_id: string }) => rpc("jmp_complete", { p_jmp_id: v.jmp_id }), onSuccess: done });
  return { start, checkIn, complete };
}

export function fmtClock(iso: string | null | undefined) {
  return iso ? new Date(iso).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" }) : "";
}
export function minutesUntil(iso: string | null | undefined): number | null {
  if (!iso) return null;
  return Math.round((new Date(iso).getTime() - Date.now()) / 60000);
}

/** One line on where a plan stands — "Next check-in 14:30 (in 42 min)", "Check-in overdue by 20 min", "Not started". */
export function jmpStandingLine(j: JmpLive): { text: string; tone: "ok" | "warn" | "danger" | "muted" } {
  if (j.status === "planned" || !j.next_due) return { text: "Planned — not started yet", tone: "muted" };
  const m = minutesUntil(j.next_due) ?? 0;
  if (j.next_state === "missed" || j.status === "escalated") return { text: `Check-in overdue by ${Math.abs(m)} min${j.status === "escalated" ? " · escalated" : ""}`, tone: "danger" };
  if (j.next_state === "due") return { text: `Check-in due now (${Math.abs(m)} min ago)`, tone: "warn" };
  return { text: `Next check-in ${fmtClock(j.next_due)} · in ${m} min`, tone: m <= 15 ? "warn" : "ok" };
}

/**
 * The live journey card — on the field home and the Site Folder. One tap
 * checks in (with the GPS fix if the caller has one); the card opens the plan.
 */
export function JmpLiveCard({ j, mine, returnTo, fix, compact }: {
  j: JmpLive; mine: boolean; returnTo?: string | null; fix?: { latitude: number; longitude: number } | null; compact?: boolean;
}) {
  const { start, checkIn } = useJmpActions();
  const s = jmpStandingLine(j);
  const toneCls = s.tone === "danger" ? "border-danger/60 bg-danger/10" : s.tone === "warn" ? "border-warn/60 bg-warn/8" : "border-info/40 bg-info/5";
  const Icon = s.tone === "danger" ? AlertTriangle : s.tone === "warn" ? Clock : Navigation;
  const iconCls = s.tone === "danger" ? "text-danger" : s.tone === "warn" ? "text-warn" : "text-info";
  const href = returnTo ? `/jmp/${j.jmp_id}?return=${encodeURIComponent(returnTo)}` : `/jmp/${j.jmp_id}`;
  const busy = start.isPending || checkIn.isPending;
  const act = () => {
    const onError = (e: any) => window.alert(e?.message ?? "Could not check in");
    if (j.status === "planned") start.mutate({ jmp_id: j.jmp_id }, { onError });
    else checkIn.mutate({ jmp_id: j.jmp_id, lat: fix?.latitude ?? null, lng: fix?.longitude ?? null }, { onError });
  };
  return (
    <div className={`rounded-2xl border p-3.5 flex items-center gap-3 ${toneCls}`}>
      <Link to={href} className="flex-1 min-w-0 flex items-center gap-3">
        <span className={`w-10 h-10 rounded-xl bg-ink/60 flex items-center justify-center shrink-0 ${iconCls}`}><Icon size={20}/></span>
        <span className="flex-1 min-w-0">
          <span className="block text-[13px] font-semibold text-text truncate">{mine ? "Your journey" : `${j.traveller_name}'s journey`} · {j.arrive_location}</span>
          <span className={`block text-[12px] font-medium ${s.tone === "danger" ? "text-danger" : s.tone === "warn" ? "text-warn" : s.tone === "ok" ? "text-ok" : "text-text-muted"}`}>{s.text}</span>
          {!compact && <span className="block text-[11px] text-text-muted font-mono">{j.jmp_number} · every {j.checkin_interval_minutes} min{j.last_checked_in_at ? ` · last ${fmtClock(j.last_checked_in_at)}` : ""}</span>}
        </span>
      </Link>
      {mine
        ? <Button size="sm" variant={s.tone === "danger" || s.tone === "warn" ? "primary" : "secondary"} icon={CheckCircle2} disabled={busy} onClick={act}>{busy ? "…" : j.status === "planned" ? "Start" : "Check in"}</Button>
        : <Link to={href}><ChevronRight size={18} className="text-text-muted"/></Link>}
    </div>
  );
}
